import { PortalType, type User } from '@/types/composition-functions';

/** Where a signed-in staff/guardian account lands. */
export type UserHomeRoute = 'parent-home' | 'admin-home';

export const userHomeRoute = (isGuardianOnly: boolean): UserHomeRoute =>
  isGuardianOnly ? 'parent-home' : 'admin-home';

/** Portals the login response reported per school id. */
export type SchoolPortalMap = Record<string, PortalType[]>;

/**
 * The parts of the current user the portal decisions read — the generated
 * User, loosened because the store also holds Students and login payloads.
 */
export type PortalUser = Partial<Pick<User, 'guardianProfile' | 'permissions'>> & {
  __typename?: string;
  availablePortals?: ReadonlyArray<string | null> | null;
};

const KNOWN_PORTALS = new Set<string>(Object.values(PortalType));

/** Keeps only values the backend's PortalType enum defines. */
export function toPortalTypes(values: ReadonlyArray<string | null | undefined> | null | undefined): PortalType[] {
  return (values ?? []).filter((v): v is PortalType => !!v && KNOWN_PORTALS.has(v));
}

export function buildSchoolPortalMap(
  loginSchools: ReadonlyArray<{ school?: { id?: string | number | null } | null; portals?: ReadonlyArray<string | null> | null }>,
): SchoolPortalMap {
  const map: SchoolPortalMap = {};
  for (const entry of loginSchools) {
    const id = entry.school?.id;
    if (id === null || id === undefined || id === '') continue;
    map[String(id)] = toPortalTypes(entry.portals);
  }
  return map;
}

/**
 * The portals the user has in the current school. `availablePortals` from the
 * me-query is authoritative and survives a reload; the login-time map is only
 * a fallback for the moment between login and that query resolving.
 */
export function resolveAvailablePortals(
  user: PortalUser | null | undefined,
  schoolId: string | number | null | undefined,
  loginSchoolPortals: SchoolPortalMap,
): PortalType[] {
  if (!user || user.__typename !== 'User') return [];
  const fromUser = toPortalTypes(user.availablePortals);
  if (fromUser.length) return fromUser;
  if (schoolId !== null && schoolId !== undefined && schoolId !== '') {
    return loginSchoolPortals[String(schoolId)] ?? [];
  }
  return [];
}

/** Whether the user may use the guardian portal at all. */
export function hasGuardianAccess(user: PortalUser | null | undefined, portals: ReadonlyArray<PortalType>): boolean {
  if (portals.includes(PortalType.Guardian)) return true;
  if (!user || user.__typename !== 'User') return false;
  return !!user.guardianProfile;
}

/** A guardian with no staff role here — never shown the admin dashboard. */
export function isGuardianOnlyUser(user: PortalUser | null | undefined, portals: ReadonlyArray<PortalType>): boolean {
  if (portals.length) {
    return portals.includes(PortalType.Guardian) && !portals.includes(PortalType.Staff);
  }
  // Portals not reported yet: a guardian profile and no staff permissions.
  if (!hasGuardianAccess(user, portals)) return false;
  return !user?.permissions?.length;
}

export function isDualPortalUser(portals: ReadonlyArray<PortalType>): boolean {
  return portals.includes(PortalType.Staff) && portals.includes(PortalType.Guardian);
}
